import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MdClose, MdSearch } from "react-icons/md";
import { getAvatarUrl, getImageUrl } from "src/Common/common";

const DEFAULT_AVATAR = "/images/common/default_avatar.png";

// Resolve a user's avatar field to a URL, falling back to the bundled default.
const resolveAvatar = (person) =>
  person?.avatar ? getAvatarUrl(person.avatar) : getImageUrl(DEFAULT_AVATAR);

/**
 * Chip-style recipients input with a selectable user dropdown matching the new
 * report-center design.
 *
 * recipients: array of currently selected { id, name, email, image }
 * users:      array of selectable users { id, name, email, image }
 * onAdd(email):     called when a free-typed email is committed (Enter / comma)
 * onToggle(user):   called when a user row / checkbox is clicked
 * onSelectAll(checked): called when the "Select All" row is toggled
 * onRemove(recipient):  called when a chip's "x" is clicked
 */
const RecipientsChipInput = ({
  recipients = [],
  users = [],
  onAdd,
  onToggle,
  onSelectAll,
  onRemove,
  placeholder,
}) => {
  const [value, setValue] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const [menuRect, setMenuRect] = useState(null);

  // Reset the search query whenever the dropdown closes.
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  // Close when clicking outside both the input and the portaled menu.
  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const updateMenuRect = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const GAP = 4;
    // Max height the menu can grow to (must match max-h-[270px] below).
    const MENU_MAX_HEIGHT = 270;
    const spaceBelow = window.innerHeight - rect.bottom - GAP;
    const spaceAbove = rect.top - GAP;
    // Flip above the input when there isn't room below but there is above.
    const flipUp = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
    // The space the menu may occupy in its chosen direction, so a cramped
    // viewport still scrolls inside the menu instead of overflowing offscreen.
    const available = Math.max(120, Math.min(MENU_MAX_HEIGHT, flipUp ? spaceAbove : spaceBelow));
    // Round to whole pixels and skip no-op updates so the portal's position
    // doesn't oscillate on subpixel rect changes (which makes content shimmer).
    const next = flipUp
      ? {
          // Anchor from the bottom so the menu grows upward from the input.
          bottom: Math.round(window.innerHeight - rect.top + GAP),
          top: null,
          left: Math.round(rect.left),
          width: Math.round(rect.width),
          maxHeight: Math.round(available),
        }
      : {
          top: Math.round(rect.bottom + GAP),
          bottom: null,
          left: Math.round(rect.left),
          width: Math.round(rect.width),
          maxHeight: Math.round(available),
        };
    setMenuRect((prev) =>
      prev &&
      prev.top === next.top &&
      prev.bottom === next.bottom &&
      prev.left === next.left &&
      prev.width === next.width &&
      prev.maxHeight === next.maxHeight
        ? prev
        : next
    );
  }, []);

  // Keep the portal-rendered dropdown aligned with the input while it's open.
  useLayoutEffect(() => {
    if (!open) return undefined;
    updateMenuRect();
    window.addEventListener("scroll", updateMenuRect, true);
    window.addEventListener("resize", updateMenuRect);
    return () => {
      window.removeEventListener("scroll", updateMenuRect, true);
      window.removeEventListener("resize", updateMenuRect);
    };
  }, [open, updateMenuRect]);

  const isSelected = (user) =>
    recipients.some((recipient) => (recipient.id ?? recipient.email) === (user.id ?? user.email));

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter(
      (user) =>
        user.name?.toLowerCase().includes(query) || user.email?.toLowerCase().includes(query)
    );
  }, [users, search]);

  const allSelected = filteredUsers.length > 0 && filteredUsers.every(isSelected);

  const commit = () => {
    const trimmed = value.trim().replace(/,$/, "").trim();
    if (trimmed && onAdd) {
      onAdd(trimmed);
    }
    setValue("");
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit();
    } else if (event.key === "Backspace" && value === "" && recipients.length > 0) {
      onRemove?.(recipients[recipients.length - 1]);
    }
  };

  const menuOpen = open && (filteredUsers.length > 0 || onSelectAll);

  return (
    <div className="w-full flex flex-col gap-2">
        <div
          ref={anchorRef}
          className="w-full h-10 bg-white rounded-[6px] outline outline-[1px] outline-offset-[-1px] outline-[#E5E8F3] px-3 flex items-center cursor-text overflow-hidden"
          onClick={() => setOpen(true)}
        >
          <input
            className="w-full outline-none bg-transparent font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6]"
            placeholder={placeholder || "Enter email addresses or select users..."}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
          />
        </div>

        {menuOpen &&
          menuRect &&
          createPortal(
            <div
              ref={menuRef}
              data-scrollable="true"
              data-recipients-dropdown="true"
              className="fixed z-[60] overflow-y-auto py-1 bg-white rounded-[8px] border border-[#C9CEE2] shadow-[0px_4px_8px_rgba(47,43,67,0.10)]"
              style={{
                top: menuRect.top ?? undefined,
                bottom: menuRect.bottom ?? undefined,
                left: menuRect.left,
                width: menuRect.width,
                maxHeight: menuRect.maxHeight,
              }}
            >
              <div className="sticky top-0 z-10 bg-white px-3 pt-1 pb-2">
                <div className="w-full h-9 bg-white rounded-[6px] border border-[#E5E8F3] px-3 flex items-center gap-2">
                  <MdSearch size={18} className="text-[#868DA6] shrink-0" />
                  <input
                    autoFocus
                    className="w-full outline-none bg-transparent font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6]"
                    placeholder="Search users..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="text-[#868DA6] hover:text-[#1A2332] shrink-0"
                    >
                      <MdClose size={16} />
                    </button>
                  )}
                </div>
              </div>

              {onSelectAll && filteredUsers.length > 0 && (
                <DropdownRow checked={allSelected} onClick={() => onSelectAll(!allSelected)}>
                  <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#5F6477] capitalize">
                    Select All
                  </span>
                </DropdownRow>
              )}

              {filteredUsers.map((user) => (
                <DropdownRow
                  key={user.id ?? user.email}
                  checked={isSelected(user)}
                  onClick={() => onToggle?.(user)}
                >
                  <div className="flex-1 flex items-center gap-3 overflow-hidden">
                    <img
                      src={resolveAvatar(user)}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover shrink-0"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = getImageUrl(DEFAULT_AVATAR);
                      }}
                    />
                    <div className="flex-1 flex flex-col overflow-hidden">
                      <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#5F6477] capitalize truncate">
                        {user.name}
                      </span>
                      {user.email && (
                        <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#868DA6] truncate">
                          {user.email}
                        </span>
                      )}
                    </div>
                  </div>
                </DropdownRow>
              ))}

              {filteredUsers.length === 0 && (
                <div className="px-4 py-[10px] font-OpenSansRegular text-[14px] leading-[20px] text-[#868DA6]">
                  No matching users
                </div>
              )}
            </div>,
            document.body
          )}

        {recipients.length > 0 && (
          <div className="w-full flex flex-wrap content-start items-start gap-2">
            {recipients.map((recipient, index) => (
              <div
                key={recipient.id ?? recipient.email ?? index}
                className="flex items-center gap-2 bg-[#EEF0F9] rounded-[6px] px-2 py-[6px] overflow-hidden"
              >
                <img
                  src={resolveAvatar(recipient)}
                  alt=""
                  className="w-5 h-5 rounded-full object-cover"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = getImageUrl(DEFAULT_AVATAR);
                  }}
                />
                <span className="flex flex-col overflow-hidden">
                  {recipient.name && (
                    <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#1A2332] truncate">
                      {recipient.name}
                    </span>
                  )}
                  {(recipient.email || !recipient.name) && (
                    <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477] truncate">
                      {recipient.email || recipient.name}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove?.(recipient);
                  }}
                  className="text-[#7C829A] hover:text-[#1A2332]"
                >
                  <MdClose size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

/**
 * A single selectable row (checkbox + content) inside the recipients dropdown.
 */
const DropdownRow = ({ checked, onClick, children }) => (
  <div
    onClick={onClick}
    className="w-full px-4 py-[10px] flex items-center gap-2 overflow-hidden cursor-pointer hover:bg-[#EEF0F9]"
  >
    <div className="pt-[3px] pb-[3px] pr-1">
      {checked ? (
        <div className="w-[18px] h-[18px] rounded-[4px] bg-[#0065FD] flex items-center justify-center">
          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
            <path
              d="M1 4.5L4 7.5L10 1.5"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      ) : (
        <div className="w-[18px] h-[18px] rounded-[4px] bg-white border border-[#C9CEE2]" />
      )}
    </div>
    {children}
  </div>
);

export default RecipientsChipInput;
